import { useCallback, useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import { LiveChatJoinResponse } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import { AudioActivityData } from "@scrile/streaming-client/build/AudioAnalyzer";
import { TrackOptions } from "@scrile/streaming-client/build/ConsumerClient";
import useStreamHelper from "./useStreamHelper";
import { SendDataEvent } from "../const";
import { getScreenTracks, getVideoTracks, removeTracksFromStream } from "../lib/mediaStreamHelpers";
import providers from "../lib/providers";
import { MediaConstraints, ProduceUserData } from "../types";

type TrackStates = { video: boolean; audio: boolean; screen: boolean };

type HandlerSet = {
  "producers-updated": (data: Required<TrackOptions>) => void;
  "audio-activity": (data: Partial<AudioActivityData>) => void;
  "track-enabled": (data: TrackStates) => void;
};

interface ConsumeProps {
  token: string;
  joinData: LiveChatJoinResponse | null;
}

const MAX_CONSUME_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 2000;

function mergeStreams(
  stream: MediaStream | undefined,
  prevVideo: MediaStream,
  prevScreen: MediaStream,
  checkStreamForChanges: (remote: MediaStream, local: MediaStream) => void
) {
  if (!stream) return;
  const newVideoStream = new MediaStream([...getVideoTracks(stream), ...stream.getAudioTracks()]);
  const newScreenStream = new MediaStream(getScreenTracks(stream));
  checkStreamForChanges(newVideoStream, prevVideo);
  checkStreamForChanges(newScreenStream, prevScreen);
}

export default function useConsumeController({ token, joinData }: ConsumeProps) {
  const { checkStreamForChanges } = useStreamHelper();

  // Ref mirrors for fresh access inside async callbacks and setUsers updaters
  const joinDataRef = useRef(joinData);
  const consumeUserIds = useRef<string[]>([]);
  const usersRef = useRef<Record<string, ProduceUserData>>({});
  const webRTCHandlersRef = useRef<Record<string, HandlerSet>>({});
  const producersRef = useRef<Record<string, TrackStates>>({});
  const trackEnabledRef = useRef<Record<string, TrackStates>>({});
  const retryCounters = useRef<Record<string, number>>({});
  const retryTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const [users, setUsers] = useState<Record<string, ProduceUserData>>({});
  const [isProducerFinishedStream, setIsProducerFinishedStream] = useState(false);

  useEffect(() => {
    joinDataRef.current = joinData;
  }, [joinData]);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  const computeConstrains = useCallback((userId: string): MediaConstraints | null => {
    const producers = producersRef.current[userId];
    if (!producers) return null;
    const enabled = trackEnabledRef.current[userId];
    return {
      videoEnabled: producers.video,
      audioEnabled: producers.audio && (enabled?.audio ?? true),
      shareEnabled: producers.screen,
    };
  }, []);

  const updateUser = useCallback(
    (broadcasterChatUserId: string, { stream, ...data }: Partial<ProduceUserData>) => {
      const user = joinDataRef.current?.users.find((i) => i.id === broadcasterChatUserId);
      if (!user) return;
      setUsers((prevState) => {
        const prev = prevState[broadcasterChatUserId];
        const localStream = prev?.stream ?? new MediaStream([]);
        const localScreenStream = prev?.screenStream ?? new MediaStream([]);
        mergeStreams(stream, localStream, localScreenStream, checkStreamForChanges);

        return {
          ...prevState,
          [broadcasterChatUserId]: {
            ...prev,
            user,
            ...data,
            stream: localStream,
            screenStream: localScreenStream,
            constrains: computeConstrains(broadcasterChatUserId) ?? prev?.constrains ?? null,
          },
        };
      });
    },
    [checkStreamForChanges, computeConstrains]
  );

  const cleanupUser = useCallback((user: ProduceUserData, id: string) => {
    user.WebRTC.closeAllStreams();
    user.stream && removeTracksFromStream(user.stream, user.stream.getTracks());
    user.screenStream && removeTracksFromStream(user.screenStream, user.screenStream.getTracks());

    const handlers = webRTCHandlersRef.current[id];
    if (handlers) {
      user.WebRTC.off("producers-updated", handlers["producers-updated"]);
      user.WebRTC.off("audio-activity", handlers["audio-activity"]);
      user.WebRTC.off("track-enabled", handlers["track-enabled"]);
    }

    delete webRTCHandlersRef.current[id];
    delete producersRef.current[id];
    delete trackEnabledRef.current[id];
  }, []);

  const removeUser = useCallback(
    (id: string) => {
      const user = usersRef.current[id];
      if (user) cleanupUser(user, id);

      setUsers((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      consumeUserIds.current = consumeUserIds.current.filter((i) => i !== id);
    },
    [cleanupUser]
  );

  const scheduleRetry = useCallback((broadcasterChatUserId: string) => {
    const count = retryCounters.current[broadcasterChatUserId] ?? 0;
    if (count >= MAX_CONSUME_RETRIES) {
      // eslint-disable-next-line no-console
      console.warn(`[consume] Max retries (${MAX_CONSUME_RETRIES}) reached for user ${broadcasterChatUserId}`);
      delete retryCounters.current[broadcasterChatUserId];
      return;
    }
    retryCounters.current[broadcasterChatUserId] = count + 1;
    const delay = RETRY_BASE_DELAY_MS * Math.pow(2, count);
    // eslint-disable-next-line no-console
    console.info(`[consume] Retry ${count + 1}/${MAX_CONSUME_RETRIES} for ${broadcasterChatUserId} in ${delay}ms`);
    retryTimers.current[broadcasterChatUserId] = setTimeout(() => {
      delete retryTimers.current[broadcasterChatUserId];
      consumeUserIds.current = consumeUserIds.current.filter((id) => id !== broadcasterChatUserId);
      const user = joinDataRef.current?.users.find((i) => i.id === broadcasterChatUserId);
      if (user?.broadcasting) {
        void onConsumeStream(broadcasterChatUserId);
      }
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onData = (data: { event: SendDataEvent }) => {
    if (data.event === SendDataEvent.PRODUCER_FINISHED_STREAM) {
      setIsProducerFinishedStream(true);
    }
  };

  const onConsumeStream = useCallback(
    async (broadcasterChatUserId: string) => {
      setIsProducerFinishedStream(false);
      if (consumeUserIds.current.includes(broadcasterChatUserId)) return;
      consumeUserIds.current.push(broadcasterChatUserId);
      try {
        const connection = await providers.LivechatsProvider.watch({
          token,
          broadcasterChatUserId,
        });
        const WebRTC = new StreamingHelper();
        const stream = await WebRTC.consumeStream({
          connection,
          tracks: { audio: false, video: false, screen: false },
          onData,
        });

        // Success — reset retry counter
        delete retryCounters.current[broadcasterChatUserId];
        updateUser(broadcasterChatUserId, { stream, connection, WebRTC, isSpeaking: false });

        const onProducersUpdated = async (data: Required<TrackOptions>) => {
          try {
            const newStream = await WebRTC.consumeStream({
              connection,
              tracks: { audio: data.audio, video: data.video, screen: data.screen },
            });
            producersRef.current[broadcasterChatUserId] = {
              video: data.video,
              audio: data.audio,
              screen: data.screen,
            };
            updateUser(broadcasterChatUserId, { stream: newStream });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`[consume] Failed to update tracks for ${broadcasterChatUserId}:`, e);
          }
        };

        const onAudioActivity = (data: Partial<AudioActivityData>) => {
          const next = !!data?.isSpeaking;
          setUsers((prev) => {
            const current = prev[broadcasterChatUserId];
            if (!current || current.isSpeaking === next) return prev;
            return { ...prev, [broadcasterChatUserId]: { ...current, isSpeaking: next } };
          });
        };

        const onTrackEnabled = (data: TrackStates) => {
          trackEnabledRef.current[broadcasterChatUserId] = data;
          if (producersRef.current[broadcasterChatUserId]) {
            updateUser(broadcasterChatUserId, {});
          }
        };

        webRTCHandlersRef.current[broadcasterChatUserId] = {
          "producers-updated": onProducersUpdated,
          "audio-activity": onAudioActivity,
          "track-enabled": onTrackEnabled,
        };
        WebRTC.on("producers-updated", onProducersUpdated);
        WebRTC.on("audio-activity", onAudioActivity);
        WebRTC.on("track-enabled", onTrackEnabled);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[consume] Failed to watch user ${broadcasterChatUserId}:`, e);
        consumeUserIds.current = consumeUserIds.current.filter((id) => id !== broadcasterChatUserId);
        scheduleRetry(broadcasterChatUserId);
      }
    },
    [token, updateUser, scheduleRetry]
  );

  const closeAllStream = useCallback(() => {
    Object.values(retryTimers.current).forEach(clearTimeout);
    retryTimers.current = {};
    retryCounters.current = {};
    for (const userId of Object.keys(usersRef.current)) {
      removeUser(userId);
    }
  }, [removeUser]);

  useEffect(() => {
    joinData &&
      joinData.users
        .filter((i) => i.id !== joinData.me.id)
        .forEach((i) => {
          if (i.broadcasting) {
            void onConsumeStream(i.id);
          }
        });
  }, [joinData, onConsumeStream]);

  useEffect(() => {
    if (!joinData) return;
    const activeUserIds = joinData.users.filter((i) => i.id !== joinData.me.id).map((i) => i.id);
    consumeUserIds.current.forEach((id) => !activeUserIds.includes(id) && removeUser(id));
  }, [joinData, removeUser, users]);

  useUnmount(closeAllStream);

  return {
    users,
    closeStream: closeAllStream,
    isProducerFinishedStream,
  };
}
